import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme, Snackbar } from '@mui/joy';
import CustomInput from "../../components/UI/custom_elements/customInput";
import PrimaryButton from "../../components/UI/custom_elements/primaryButton";
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const RecoverPassword = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const token = searchParams.get('token');
    const source = searchParams.get('source');
    const username = searchParams.get('username');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (!token || !source || !username) {
            setErrorMessage('Enlace inválido. Faltan parámetros requeridos.');
        }
    }, [token, source, username]);

    const handleRecoverPassword = async () => {
        if (!newPassword.trim()) {
            setErrorMessage('Por favor ingrese la nueva contraseña');
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrorMessage('Las contraseñas no coinciden');
            return;
        }

        if (newPassword.length < 6) {
            setErrorMessage('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        if (!token || !source || !username) {
            setErrorMessage('Datos incompletos. Por favor use el enlace del correo.');
            return;
        }

        setLoading(true);
        setErrorMessage('');
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_URL_API}/orders/recover-password`,
                {
                    token: token,
                    username: username,
                    source: source,
                    new_password: newPassword
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data && response.data.success) {
                setSuccessMessage('Contraseña actualizada exitosamente');
                setTimeout(() => {
                    navigate('/');
                }, 2000);
            }
        } catch (error) {
            if (error.response && error.response.data) {
                if (error.response.data.success === false) {
                    setErrorMessage(error.response.data.message || 'Token inválido o expirado');
                } else {
                    setErrorMessage('Error al actualizar la contraseña');
                }
            } else {
                setErrorMessage('Error al actualizar la contraseña');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box 
            sx={{
                width: "100vw", 
                height: "100dvh", 
                backgroundColor: theme.vars.palette.background.body, 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3
            }}
        >
            <Snackbar
                open={!!errorMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                variant="solid"
                color="danger"
                autoHideDuration={4000}
                onClose={() => setErrorMessage('')}
            >
                {errorMessage}
            </Snackbar>

            <Snackbar
                open={!!successMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                variant="solid"
                color="success"
                autoHideDuration={4000}
                onClose={() => setSuccessMessage('')}
            >
                {successMessage}
            </Snackbar>

            <Box sx={{
                width: "100%",
                maxWidth: "500px",
                backgroundColor: theme.vars.palette.background.surface,
                borderRadius: '12px',
                p: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}>
                <Typography sx={{
                    fontFamily: "Montserrat",
                    fontSize: "24px",
                    fontWeight: 700,
                    color: theme.vars.palette.primary["500"],
                    mb: 1
                }}>
                    Recuperar Contraseña
                </Typography>

                <Typography sx={{
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    color: theme.vars.palette.text.main,
                    mb: 4
                }}>
                    Ingrese su nueva contraseña para recuperar el acceso a su cuenta.
                </Typography>

                <Box mt={3}>
                    <Typography mb={1} sx={{
                        fontFamily: "Montserrat",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: theme.vars.palette.text.main
                    }}>
                        Nueva Contraseña
                    </Typography>
                    <CustomInput
                        placeholder="Ingrese la nueva contraseña"
                        width="100%"
                        password={true}
                        value={newPassword}
                        setValue={setNewPassword}
                    />
                </Box>

                <Box mt={3}>
                    <Typography mb={1} sx={{
                        fontFamily: "Montserrat",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: theme.vars.palette.text.main
                    }}>
                        Confirmar Contraseña
                    </Typography>
                    <CustomInput
                        placeholder="Confirme la nueva contraseña"
                        width="100%"
                        password={true}
                        value={confirmPassword}
                        setValue={setConfirmPassword}
                    />
                </Box>

                <Box mt={4} width="100%">
                    <PrimaryButton
                        click={handleRecoverPassword}
                        text="Actualizar Contraseña"
                        full={true}
                        loading={loading}
                        desactived={!newPassword || !confirmPassword || !token || !source || !username}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default RecoverPassword;
